import { useEffect } from "react";
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { calculatePaymentsTotal, validatePayments, getDefaultPaymentDate } from "../utils";
import { PaymentsHeader } from "./PaymentsHeader";
import { PaymentRow } from "./PaymentRow";
import { findDeleteLockedReason, useChargedOrderPayments } from "./chargedOrderPayments";

type EditableOrderPaymentsProps = {
    orderId?: number;
    orderTotal: number;
    entityForm: UseFormReturn;
    paymentStatus: string;
    recordType: "create" | "update" | "detail";
    paymentMethodField: any;
    amountField: any;
    existingPayments?: Array<any>;
};

export const EditableOrderPayments = ({
    orderId,
    orderTotal,
    entityForm,
    paymentStatus,
    paymentMethodField,
    amountField,
    existingPayments = [],
}: EditableOrderPaymentsProps) => {
    const { fields, append, remove, replace } = useFieldArray({
        control: entityForm.control,
        name: "order_payments",
    });

    useEffect(() => {
        entityForm.setValue("order_payments_to_delete", []);
    }, [entityForm]);

    useEffect(() => {
        if (paymentStatus !== "paid" || orderTotal === 0) return;

        const rowsBelongToThisOrder = fields.length > 0 && fields.every((row: any) => row.order === orderId);
        if (rowsBelongToThisOrder) return;

        const hasExistingPayments = existingPayments.length > 0;
        const initialPayments = hasExistingPayments
            ? existingPayments.map((payment: any) => ({
                  order: payment.order,
                  order_payment_id: payment.id,
                  payment_method: payment.payment_method,
                  amount: payment.amount,
                  payment_date: payment.payment_date,
              }))
            : [{ order: orderId, payment_method: "", amount: orderTotal, payment_date: getDefaultPaymentDate() }];

        const hasForeignRows = fields.length > 0;
        replace(initialPayments);
        if (hasForeignRows) entityForm.setValue("order_payments_to_delete", []);
    }, [orderId, paymentStatus, orderTotal, existingPayments, replace, fields, entityForm]);

    const { data: chargedPayments = [] } = useChargedOrderPayments(orderId);

    const payments = entityForm.watch("order_payments") || [];
    const paymentsTotal = calculatePaymentsTotal(payments);
    const validation = validatePayments(payments, orderTotal);
    const choices = paymentMethodField.settings?.options?.choices || [];

    const handleAddPayment = () => {
        append({
            order: orderId,
            payment_method: "",
            amount: 0,
            payment_date: getDefaultPaymentDate(),
        });
    };

    const handleDeletePayment = (index: number) => {
        const payment = payments[index];
        if (payment?.order_payment_id) {
            const currentToDelete = entityForm.getValues("order_payments_to_delete") || [];
            entityForm.setValue("order_payments_to_delete", [...currentToDelete, payment.order_payment_id]);
        }
        remove(index);
    };

    return (
        <Box mt={3} ml={0.5} p={2} sx={{ backgroundColor: "#fff" }}>
            <PaymentsHeader
                paymentsTotal={paymentsTotal}
                orderTotal={orderTotal}
                onAddPayment={handleAddPayment}
                showAddButton={true}
            />

            {!validation.isValid && (
                <Alert severity={validation.difference && validation.difference > 0 ? "error" : "warning"} sx={{ mb: 2 }}>
                    {validation.error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '25%' }}>Data</TableCell>
                            <TableCell sx={{ width: '35%' }}>Método de Pagamento</TableCell>
                            <TableCell sx={{ width: '25%' }}>Valor</TableCell>
                            <TableCell align="right" sx={{ width: '15%' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map((field, index) => (
                            <PaymentRow
                                key={field.id}
                                payment={payments[index]}
                                index={index}
                                paymentMethodField={paymentMethodField}
                                amountField={amountField}
                                choices={choices}
                                entityForm={entityForm}
                                isReadOnly={false}
                                onDelete={handleDeletePayment}
                                deleteLockedReason={findDeleteLockedReason(payments[index]?.order_payment_id, chargedPayments)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

